import { useEffect, useRef, useState } from 'react'
import { useGraphic } from '../../../lib/features/graphic/GraphicContext'
import { addStyleToPreventSelection } from '../utils'

// A component to render the iframe for the graphics preview, and handle the events for the iframe
const GraphicsPreviewIframe = ({
  handleScroll,
  handleMouseDown,
  handleDoubleClick,
  handleKeyDown,
  onHtmlRefreshed,
  onIframeReady,
}: {
  handleScroll: (iframeDoc: Document) => void
  handleMouseDown: (event: MouseEvent, iframeDoc: Document) => void
  handleDoubleClick: (event: MouseEvent, iframeDoc: Document) => void
  handleKeyDown: (event: KeyboardEvent, iframeDoc: Document) => void
  onHtmlRefreshed: (iframe: HTMLIFrameElement) => void
  onIframeReady?: (iframe: HTMLIFrameElement | null) => void
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [contentWidth, setContentWidth] = useState<number | undefined>(undefined)
  const [contentHeight, setContentHeight] = useState<number | undefined>(undefined)

  const { graphic } = useGraphic()

  let iFrameStyle: React.CSSProperties

  if (contentWidth && contentHeight) {
    iFrameStyle = {
      width: `${contentWidth}px`,
      height: `${contentHeight}px`,
      overflow: 'hidden',
    }
  } else {
    // Setting to 0px width and height until we calculate the dimensions
    iFrameStyle = {
      width: `0px`,
      height: `0px`,
      overflow: 'hidden',
    }
  }

  if (graphic?.config?.type === 'image') {
    iFrameStyle.backgroundColor = 'white'
  }

  // Call the callback whenever the iframe ref changes
  useEffect(() => {
    if (onIframeReady) {
      onIframeReady(iframeRef.current)
    }
  }, [onIframeReady])

  useEffect(() => {
    if (graphic?.html && iframeRef.current) {
      const iframe = iframeRef.current

      const doc = iframe.contentDocument || iframe.contentWindow?.document
      if (doc) {
        // Clear and write new content
        doc.open()
        doc.write(graphic.html)
        doc.close()

        addStyleToPreventSelection(doc)

        const setupListeners = () => {
          // Store function references so we can properly remove them later
          const scrollHandler = () => handleScroll(doc)
          const resizeHandler = () => handleScroll(doc)
          const mouseDownHandler = (event: MouseEvent) => handleMouseDown(event, doc)
          const doubleClickHandler = (event: MouseEvent) => handleDoubleClick(event, doc)
          const keyDownHandler = (event: KeyboardEvent) => handleKeyDown(event, doc)

          if (iframe.contentWindow) {
            iframe.contentWindow.addEventListener('scroll', scrollHandler)
            iframe.contentWindow.addEventListener('resize', resizeHandler)

            doc.addEventListener('mousedown', mouseDownHandler)

            doc.addEventListener('dblclick', doubleClickHandler)
            doc.addEventListener('keydown', keyDownHandler)
          }

          return () => {
            if (iframe.contentWindow) {
              iframe.contentWindow.removeEventListener('scroll', scrollHandler)
              iframe.contentWindow.removeEventListener('resize', resizeHandler)
            }
            doc.removeEventListener('mousedown', mouseDownHandler)
            doc.removeEventListener('dblclick', doubleClickHandler)
            doc.removeEventListener('keydown', keyDownHandler)
          }
        }

        // Set the width and height to 0, we will update this when the iframe is ready
        setContentWidth(0)
        setContentHeight(0)

        // Wait for the iframe to be ready, then get the width and height
        setTimeout(() => {
          if (iframeRef.current) {
            const doc =
              iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document
            if (doc) {
              setContentWidth(doc.documentElement.scrollWidth || doc.body?.scrollWidth)
              setContentHeight(doc.documentElement.scrollHeight || doc.body?.scrollHeight)
            }
          }
        }, 300)

        // Setup listeners (returns the cleanup function)
        const cleanupListeners = setupListeners()

        const documentKeyDownHandler = (event: KeyboardEvent) => handleKeyDown(event, doc)
        document.addEventListener('keydown', documentKeyDownHandler)

        onHtmlRefreshed(iframe)

        return () => {
          cleanupListeners()
          document.removeEventListener('keydown', documentKeyDownHandler)
        }
      }
    }
  }, [graphic?.html])

  return (
    <iframe
      ref={iframeRef}
      className="h-full border-0"
      style={iFrameStyle}
      title="Graphics Preview"
      tabIndex={0}
      scrolling="no"
    />
  )
}

export default GraphicsPreviewIframe
